import { useState } from "react";
import api from "@/utils/axios";
import cls from "../financePage.module.scss";

const buildUrl = (path, params) => {
  const base = api.defaults.baseURL || "";
  const url = new URL(`${base}${path}`, window.location.origin);
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      url.searchParams.set(key, value);
    }
  });
  return url.toString();
};

const Reports = () => {
  const [filters, setFilters] = useState({
    groupBy: "day",
    startDate: "",
    endDate: "",
    status: "approved",
    type: "",
    entryType: "income,expense",
  });
  const [cadPayments, setCadPayments] = useState([]);
  const [notyTransactions, setNotyTransactions] = useState([]);
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [cadDetails, setCadDetails] = useState([]);
  const [notyDetails, setNotyDetails] = useState([]);
  const [liabilitySummary, setLiabilitySummary] = useState(null);
  const [error, setError] = useState("");

  const handleChange = (field) => (event) => {
    setFilters((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const loadCadPayments = async () => {
    try {
      setError("");
      const res = await api.get("/v1/admin/reports/cad-payments", {
        params: {
          groupBy: filters.groupBy,
          startDate: filters.startDate || undefined,
          endDate: filters.endDate || undefined,
          status: filters.status || undefined,
        },
      });
      setCadPayments(res.data?.data ?? []);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load CAD payments report",
      );
    }
  };

  const loadNotyTransactions = async () => {
    try {
      setError("");
      const res = await api.get("/v1/admin/reports/noty-transactions", {
        params: {
          groupBy: filters.groupBy,
          startDate: filters.startDate || undefined,
          endDate: filters.endDate || undefined,
          type: filters.type || undefined,
        },
      });
      setNotyTransactions(res.data?.data ?? []);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load Noty transactions report",
      );
    }
  };

  const loadLedgerEntries = async () => {
    try {
      setError("");
      const res = await api.get("/v1/admin/reports/ledger-entries", {
        params: {
          groupBy: filters.groupBy,
          startDate: filters.startDate || undefined,
          endDate: filters.endDate || undefined,
          entryType: filters.entryType || undefined,
        },
      });
      setLedgerEntries(res.data?.data ?? []);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load ledger report",
      );
    }
  };

  const loadCadDetails = async () => {
    try {
      setError("");
      const res = await api.get("/v1/admin/reports/cad-payment-details", {
        params: {
          startDate: filters.startDate || undefined,
          endDate: filters.endDate || undefined,
          status: filters.status || undefined,
        },
      });
      setCadDetails(res.data?.data ?? []);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load CAD payment details",
      );
    }
  };

  const loadNotyDetails = async () => {
    try {
      setError("");
      const res = await api.get("/v1/admin/reports/noty-spend-details", {
        params: {
          startDate: filters.startDate || undefined,
          endDate: filters.endDate || undefined,
        },
      });
      setNotyDetails(res.data?.data ?? []);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load Noty spend details",
      );
    }
  };

  const loadLiabilitySummary = async () => {
    try {
      setError("");
      const res = await api.get("/v1/admin/reports/liability-summary");
      setLiabilitySummary(res.data?.data ?? null);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load liability summary",
      );
    }
  };

  const downloadCsv = (path, params) => {
    const url = buildUrl(path, { ...params, format: "csv" });
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className={cls.page}>
      <header className={cls.header}>
        <div className={cls.headerLeft}>
          <p className={cls.eyebrow}>Finance • Reports</p>
          <h1 className={cls.title}>Finance reports</h1>
          <p className={cls.subtitle}>
            Run grouped reports, download CSV exports, and review detail lists.
          </p>
        </div>
      </header>

      <section className={cls.card}>
        <h2 className={cls.sectionTitle}>Report filters</h2>
        <div className={cls.filters}>
          <label className={cls.formRow}>
            <span className={cls.label}>Group by</span>
            <select
              className={cls.select}
              value={filters.groupBy}
              onChange={handleChange("groupBy")}
            >
              <option value="day">Day</option>
              <option value="month">Month</option>
              <option value="year">Year</option>
            </select>
          </label>
          <label className={cls.formRow}>
            <span className={cls.label}>Start date</span>
            <input
              className={cls.input}
              type="date"
              value={filters.startDate}
              onChange={handleChange("startDate")}
            />
          </label>
          <label className={cls.formRow}>
            <span className={cls.label}>End date</span>
            <input
              className={cls.input}
              type="date"
              value={filters.endDate}
              onChange={handleChange("endDate")}
            />
          </label>
          <label className={cls.formRow}>
            <span className={cls.label}>CAD status filter</span>
            <input
              className={cls.input}
              value={filters.status}
              onChange={handleChange("status")}
              placeholder="approved"
            />
          </label>
          <label className={cls.formRow}>
            <span className={cls.label}>Noty type filter</span>
            <input
              className={cls.input}
              value={filters.type}
              onChange={handleChange("type")}
              placeholder="credit, debit"
            />
          </label>
          <label className={cls.formRow}>
            <span className={cls.label}>Ledger entry types</span>
            <input
              className={cls.input}
              value={filters.entryType}
              onChange={handleChange("entryType")}
              placeholder="income,expense"
            />
          </label>
        </div>
      </section>

      {error && (
        <div className={`${cls.stateBox} ${cls.stateBoxError}`}>
          <strong>Something went wrong</strong>
          <span>{error}</span>
        </div>
      )}

      <section className={cls.card}>
        <h2 className={cls.sectionTitle}>Summary reports</h2>
        <div className={cls.inlineActions}>
          <button className={cls.btn} onClick={loadCadPayments}>
            Load CAD payments
          </button>
          <button className={cls.btn} onClick={loadNotyTransactions}>
            Load Noty transactions
          </button>
          <button className={cls.btn} onClick={loadLedgerEntries}>
            Load ledger entries
          </button>
        </div>
        <div className={cls.gridTwo}>
          <div>
            <h3 className={cls.sectionTitle}>CAD payments</h3>
            <div className={cls.inlineActions}>
              <button
                className={cls.btnSecondary}
                onClick={() =>
                  downloadCsv("/v1/admin/reports/cad-payments", {
                    groupBy: filters.groupBy,
                    startDate: filters.startDate,
                    endDate: filters.endDate,
                    status: filters.status,
                  })
                }
              >
                Export CSV
              </button>
            </div>
            <div className={cls.previewBox}>
              {cadPayments.length
                ? JSON.stringify(cadPayments, null, 2)
                : "No data yet"}
            </div>
          </div>
          <div>
            <h3 className={cls.sectionTitle}>Noty transactions</h3>
            <div className={cls.inlineActions}>
              <button
                className={cls.btnSecondary}
                onClick={() =>
                  downloadCsv("/v1/admin/reports/noty-transactions", {
                    groupBy: filters.groupBy,
                    startDate: filters.startDate,
                    endDate: filters.endDate,
                    type: filters.type,
                  })
                }
              >
                Export CSV
              </button>
            </div>
            <div className={cls.previewBox}>
              {notyTransactions.length
                ? JSON.stringify(notyTransactions, null, 2)
                : "No data yet"}
            </div>
          </div>
          <div>
            <h3 className={cls.sectionTitle}>Ledger entries</h3>
            <div className={cls.inlineActions}>
              <button
                className={cls.btnSecondary}
                onClick={() =>
                  downloadCsv("/v1/admin/reports/ledger-entries", {
                    groupBy: filters.groupBy,
                    startDate: filters.startDate,
                    endDate: filters.endDate,
                    entryType: filters.entryType,
                  })
                }
              >
                Export CSV
              </button>
            </div>
            <div className={cls.previewBox}>
              {ledgerEntries.length
                ? JSON.stringify(ledgerEntries, null, 2)
                : "No data yet"}
            </div>
          </div>
        </div>
      </section>

      <section className={cls.card}>
        <h2 className={cls.sectionTitle}>Detail reports</h2>
        <div className={cls.inlineActions}>
          <button className={cls.btn} onClick={loadCadDetails}>
            Load CAD payment details
          </button>
          <button className={cls.btn} onClick={loadNotyDetails}>
            Load Noty spend details
          </button>
          <button className={cls.btn} onClick={loadLiabilitySummary}>
            Load liability summary
          </button>
        </div>
        <div className={cls.gridTwo}>
          <div>
            <h3 className={cls.sectionTitle}>CAD payment details</h3>
            <div className={cls.inlineActions}>
              <button
                className={cls.btnSecondary}
                onClick={() =>
                  downloadCsv("/v1/admin/reports/cad-payment-details", {
                    startDate: filters.startDate,
                    endDate: filters.endDate,
                    status: filters.status,
                  })
                }
              >
                Export CSV
              </button>
            </div>
            <div className={cls.previewBox}>
              {cadDetails.length
                ? JSON.stringify(cadDetails.slice(0, 20), null, 2)
                : "No data yet"}
            </div>
          </div>
          <div>
            <h3 className={cls.sectionTitle}>Noty spend details</h3>
            <div className={cls.inlineActions}>
              <button
                className={cls.btnSecondary}
                onClick={() =>
                  downloadCsv("/v1/admin/reports/noty-spend-details", {
                    startDate: filters.startDate,
                    endDate: filters.endDate,
                  })
                }
              >
                Export CSV
              </button>
            </div>
            <div className={cls.previewBox}>
              {notyDetails.length
                ? JSON.stringify(notyDetails.slice(0, 20), null, 2)
                : "No data yet"}
            </div>
          </div>
          <div>
            <h3 className={cls.sectionTitle}>Liability summary</h3>
            <div className={cls.previewBox}>
              {liabilitySummary
                ? JSON.stringify(liabilitySummary, null, 2)
                : "No data yet"}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Reports;
